import type {
  AdsDateRange,
  AdsOrganizeBy,
  InsightsCustomerSegment,
} from "../types";

export const ORGANIZE_BY_OPTIONS: {
  value: AdsOrganizeBy;
  label: string;
  triggerLabel: string;
}[] = [
  {
    value: "ad-click-month",
    label: "Ad-click month",
    triggerLabel: "Ad-click month",
  },
  {
    value: "order-month",
    label: "Order month",
    triggerLabel: "Order month",
  },
];

/**
 * Ordered the way someone reads a range picker: the periods you're partway
 * through, shortest first, then look-backs from shortest to longest, then all
 * time.
 */
export const DATE_RANGE_OPTIONS: {
  value: AdsDateRange;
  label: string;
  disabled?: boolean;
}[] = [
  { value: "this-quarter", label: "This quarter" },
  { value: "this-year", label: "This year" },
  { value: "quarter", label: "Last quarter" },
  { value: "6m", label: "Last 6 months" },
  { value: "12m", label: "Last 12 months" },
  { value: "lifetime", label: "Lifetime" },
];

/**
 * The cut is by the kind of ad that drove the activity, not by who the customer
 * turned out to be — a new customer ad and a reorder ad are separate campaigns
 * with their own budgets and spend. `triggerLabel` keeps the collapsed chip
 * short ("Ad type: New customer").
 *
 * The values still read `*-customers` because `InsightsCustomerSegment` is the
 * shape the data layer already segments by.
 */
export const AD_TYPE_OPTIONS: {
  value: InsightsCustomerSegment;
  label: string;
  triggerLabel: string;
}[] = [
  { value: "all-customers", label: "All ad types", triggerLabel: "All" },
  {
    value: "new-customers",
    label: "New customer ads",
    triggerLabel: "New customer",
  },
  {
    value: "returning-customers",
    label: "Reorder ads",
    triggerLabel: "Reorder",
  },
];

/**
 * V3's month-table column density. Essential is the default compact set;
 * All restores Continued sales, impressions, and clicks.
 * `triggerLabel` keeps the chip short ("Columns: Essential").
 */
export const TABLE_COLUMNS_OPTIONS: {
  value: "essential" | "all";
  label: string;
  triggerLabel: string;
}[] = [
  {
    value: "essential",
    label: "Essential metrics",
    triggerLabel: "Essential",
  },
  { value: "all", label: "All metrics", triggerLabel: "All" },
];

/**
 * Share of a trailing-12-month total each range covers, for the mock cuts that
 * hold one period's totals rather than a month-by-month series. Anchored at 1
 * for "12m", so lifetime runs above it and the shorter windows below. The two
 * current-period ranges are sized to a partial quarter and roughly half a year,
 * which is where they sit on the prototype's July anchor.
 */
export const DATE_RANGE_SCALE: Record<AdsDateRange, number> = {
  "this-quarter": 0.09,
  "this-year": 0.55,
  quarter: 0.28,
  "6m": 0.51,
  "12m": 1,
  lifetime: 1.42,
};

/**
 * The above expressed as a share of the whole program, so a cut whose totals are
 * already lifetime figures can be scaled down to a range without lifetime itself
 * moving.
 */
export function lifetimeShareForRange(range: AdsDateRange): number {
  return DATE_RANGE_SCALE[range] / DATE_RANGE_SCALE.lifetime;
}

/**
 * Inclusive month-index window for a range, anchored to the newest month the
 * advertiser has (which is in progress). Every range runs up to and including
 * that partial month except "Last quarter", which is the previous complete
 * calendar quarter and so is the one range that stops short of it.
 *
 * A window rather than a row count, so a range covers the months it names even
 * if the advertiser has a gap in the middle of their history. Lifetime has no
 * bound and returns null.
 */
export function monthWindowForRange(
  range: AdsDateRange,
  currentMonthIndex: number
): { from: number; to: number } | null {
  // Month indices count from a year boundary, so the remainder mod 12 is the
  // month of the year and mod 3 of that is the months elapsed in the calendar
  // quarter — the in-progress month included in both.
  const monthsIntoYear = currentMonthIndex % 12;
  const thisQuarterStart = currentMonthIndex - (monthsIntoYear % 3);
  const thisYearStart = currentMonthIndex - monthsIntoYear;

  switch (range) {
    case "this-quarter":
      return { from: thisQuarterStart, to: currentMonthIndex };
    case "this-year":
      return { from: thisYearStart, to: currentMonthIndex };
    case "quarter":
      return { from: thisQuarterStart - 3, to: thisQuarterStart - 1 };
    case "6m":
      return { from: currentMonthIndex - 5, to: currentMonthIndex };
    case "12m":
      return { from: currentMonthIndex - 11, to: currentMonthIndex };
    case "lifetime":
      return null;
  }
}

/**
 * The month table's window: the selected range plus the month immediately
 * before it. Last 12 months therefore runs from this month last year through
 * the in-progress month (Aug 2026 through Aug 2025 on an August anchor), so
 * a reader can put the same calendar month side by side. Lifetime is still
 * unbounded.
 */
export function monthWindowForTable(
  range: AdsDateRange,
  currentMonthIndex: number
): { from: number; to: number } | null {
  const window = monthWindowForRange(range, currentMonthIndex);
  if (!window) {
    return null;
  }
  return { from: window.from - 1, to: window.to };
}
